import React, { useState, useEffect } from 'react';
import { Button, Modal } from 'react-bootstrap';
import Dropzone from 'react-dropzone';
import update from 'immutability-helper';
import ContentLoader from 'react-content-loader'
import FontAwesomeIcon from '@fortawesome/react-fontawesome';
import faStar from '@fortawesome/fontawesome-free-solid/faStar';

import i18next from "i18next";

import config from "config";

const AssetImageGallery = ({assetImages, onAssetImageChange, onError, viewOnly}) => {
  const [internalAssetImages, setInternalAssetImages] = useState(assetImages);
  const [showAddImageModal, setShowAddImageModal] = useState(false);
  const [replacementIndex, setReplacementIndex] = useState(null);
  const toggleShowAddImageModal = () => { setShowAddImageModal(!showAddImageModal); }

  useEffect(() => { onAssetImageChange && onAssetImageChange(internalAssetImages) }, [internalAssetImages]);
 
  const deleteAssetImage = (index) => {
    const unfilteredIndex = internalAssetImages.indexOf(internalAssetImages.filter((assetImage) => !assetImage.replaced && !assetImage.deleted)[index]);
    if (internalAssetImages[unfilteredIndex].original) {
      setInternalAssetImages(update(internalAssetImages, {[unfilteredIndex]: {$merge: {deleted: true}}}));
    } else {
      setInternalAssetImages(update(internalAssetImages, {$splice: [[unfilteredIndex, 1]]}));
    }
  }

  const replaceAssetImage = (index) => {
    const unfilteredIndex = internalAssetImages.indexOf(internalAssetImages.filter((assetImage) => !assetImage.replaced && !assetImage.deleted)[index]);
    setReplacementIndex(unfilteredIndex);
    toggleShowAddImageModal();
  }

  const imageLoadError = (err) => {
    if (err.target && err.target.src) {
      console.error(`${i18next.t('messages.error-loading-image')}: ${err.target.src}`);
    } else {
      console.error(i18next.t('messages.error-unknnown-loading-image'));
    }
    onError('Error loading image.');
  }

  const onDeviceUploadDrop = (files) => {
    files.forEach((file) => {
      const reader = new FileReader();

      reader.onabort = () => onError(i18next.t('messages.aborted-local-load'));
      reader.onerror = () => onError(i18next.t('messages.error-loading-file'));
      reader.onload = () => {         
        if (replacementIndex != null) {
          if (internalAssetImages[replacementIndex].original) {
              setInternalAssetImages(update(internalAssetImages, {
                [replacementIndex]: {$merge: {replaced: true}},
                $push: [{id:null, src:reader.result, primary:false, original:false, deleted:false, replaced:false, file:file, replacesId:internalAssetImages[replacementIndex].id}]
              }));
          } else {
            setInternalAssetImages(update(internalAssetImages, {
              $splice: [[replacementIndex, 1, {id:null, src:reader.result, primary:false, original:false, deleted:false, replaced:false, file:file}]]
            }));
          }
          setReplacementIndex(null);
        } else {
          setInternalAssetImages(update(internalAssetImages, {$push: [{id:null, src:reader.result, primary:false, original:false, deleted:false, replaced:false, file:file}]}));
        }
      }

      reader.readAsDataURL(file);
    });
  }

  return (
    <div className='gallery'>
      <div className='image-header'>
        {
          (internalAssetImages && internalAssetImages.length) ?
            <AssetImageGroup assetImages={internalAssetImages} onReplaceImage={replaceAssetImage} onDeleteImage={deleteAssetImage} onImageLoadError={imageLoadError} viewOnly={viewOnly} /> :
            <div className="no-image-found-box"><img src={`${config.WEB_BASE_URL}/images/icon/no_image_icon.svg`}/><em>{ i18next.t('no-image-found') }</em></div>
        }
      </div>

      <div> </div>
      { viewOnly ? null : <Button className="btn btn-sm btn-default" style={{ width: '100%' }} onClick={toggleShowAddImageModal}>{ i18next.t('form-labels.add-asset-image') }</Button> }
      
      <AddAssetImageModal 
        show={showAddImageModal}
        toggleModal={toggleShowAddImageModal}
        onDeviceUploadDrop={onDeviceUploadDrop}
      />      
    </div>
  );
}

const AddAssetImageModal = ({show, toggleModal, onDeviceUploadDrop}) => {
  return (
    <Modal show={show} onHide={toggleModal}>
      <Modal.Header closeButton>
        <Modal.Title>{ i18next.t('pages.assets.asset-image') }</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="device-dropzone">
          <Dropzone className="dropzone-format" onDrop={(files) => { toggleModal(); onDeviceUploadDrop(files); }}>
            <div style={{textAlign: "center", top: "45%", position: "relative"}}> { i18next.t('pages.assets.drag') } </div>
            <button style={{margin: "0 auto", display: "block", position: "relative", top: "110px"}} className="btn btn-sm btn-default"> { i18next.t('pages.assets.upload') } </button>
          </Dropzone>
        </div>
      </Modal.Body>
    </Modal>    
  );
}

const AssetImageGroup = ({assetImages, onReplaceImage, onDeleteImage, onImageLoadError, viewOnly}) => {
  return (
    assetImages.filter((assetImage) => !assetImage.replaced && !assetImage.deleted)
               .map((assetImage, index) =>
      <AssetImage 
        key={index} 
        index={index} 
        isPrimary={assetImage.primary} 
        imageSrc={assetImage.src}
        onDeleteImage={onDeleteImage} 
        onReplaceImage={onReplaceImage}
        onImageLoadError={onImageLoadError}
        viewOnly={viewOnly}
      />
    )
  );
}

const AssetImage = ({index, imageSrc, isPrimary, onReplaceImage, onDeleteImage, onImageLoadError, viewOnly}) => {
  const [showZoom, setShowZoom] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  const toggleShowZoom = () => { setShowZoom(!showZoom); }

  return (
    <div>
        <div className="asset-image-container">
          <img src={ imageSrc }
               onClick={ toggleShowZoom } 
               onLoad={ () => { setImageLoading(false); } } 
               onError={(err) => { setImageLoading(false); onImageLoadError(err); }}
               className="asset-image" 
          />

          { imageLoading && <AssetImageLoading className="asset-image-loader" /> }

          { (!imageLoading && isPrimary) && <FontAwesomeIcon icon={faStar}/> }

          { !imageLoading && <img className="asset-image-zoom" src={`${config.WEB_BASE_URL}/images/icon/enlarge.svg`} onClick={toggleShowZoom} /> }
        </div>

        <ZoomAssetModal show={showZoom} toggleModal={toggleShowZoom} onReplaceImage={onReplaceImage} onDeleteImage={onDeleteImage} imageSrc={imageSrc} index={index} viewOnly={viewOnly} />
    </div>
  )
}

const AssetImageLoading = (props) => (
  <ContentLoader speed={2} width={100} height={100} viewBox="0 0 100 100" backgroundColor="#f3f3f3" foregroundColor="#ecebeb" {...props} >
    <rect x="0" y="0" rx="2" ry="2" width="100" height="100" />
  </ContentLoader>
)

const ZoomAssetModal = ({show, toggleModal, onReplaceImage, onDeleteImage, imageSrc, index, viewOnly}) => {
  return (
    <Modal bsSize='large' show={show} onHide={toggleModal}>
      <Modal.Header closeButton>
        <Modal.Title>{ i18next.t('pages.assets.asset-image') }</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <img src={imageSrc} alt={ i18next.t('pages.assets.asset-image') } className='asset-image-center' />

        <br/>

        { viewOnly ? null : <OptionalButtonGroup onReplaceImage={(index) => { toggleModal(); onReplaceImage(index); }} onDeleteImage={(index) => { toggleModal(); onDeleteImage(index); }} id={index} /> }
      </Modal.Body>
    </Modal>
  );
}

const OptionalButtonGroup = ({onReplaceImage, onDeleteImage, id}) => {
  return (
    (onReplaceImage || onDeleteImage) ? 
    <div className="asset-image-buttons">
      {onReplaceImage ? <button className="btn btn-sm btn-default" onClick={() => {onReplaceImage(id);}}>{ i18next.t('replace-image') }</button> : null}
      {onDeleteImage ? <button className="btn btn-sm btn_default red" onClick={() => {onDeleteImage(id);}}>{ i18next.t('delete-image') }</button> : null}
    </div> : null
  );
}

export default AssetImageGallery;